"use client"
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Theme,
  useMediaQuery,
} from "@mui/material"
import { useDraftProtection } from "@tc/ui-dfe/hooks/config/useDraftProtection"
import { usePermission } from "@tc/ui-permission/hooks"
import { useRouter, useSearchParams } from "next/navigation"
import { useMemo } from "react"

export interface MenuItemProps {
  active?: boolean
  children?: React.ReactNode
  icon?: React.ReactNode
  open: boolean
  path: string
  label?: string
  onClose?: () => void
  access?: string[]
  ffPath?: Record<string, string>
  isModal?: boolean
  onModalOpen?: () => void
}

export const MenuItem = (props: MenuItemProps) => {
  const {
    active,
    icon,
    path,
    label,
    onClose,
    access,
    ffPath,
    isModal,
    onModalOpen,
  } = props
  const { hasAccess, hasPermission } = usePermission()
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const router = useRouter()
  const searchParams = useSearchParams()
  const { confirmDiscardDraft } = useDraftProtection()

  const _path = useMemo(() => {
    return Object.entries(ffPath || {}).reduce((acc, [ff, ffPath]) => {
      if (hasPermission([ff])) {
        return ffPath
      }
      return acc
    }, path)
  }, [ffPath, hasPermission, path])

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()

    const confirmed = await confirmDiscardDraft()

    if (confirmed) {
      if (isModal && onModalOpen) {
        onModalOpen()
        onClose?.()
      } else {
        const qs = searchParams.toString()
        const destination =
          active && qs && _path === "/requests" ? `${_path}?${qs}` : _path
        router.push(destination)
        onClose?.()
      }
    }
  }

  if (access && !hasAccess(access)) {
    return null
  }

  return (
    <ListItemButton
      data-testid="menu-item-route"
      selected={active}
      onClick={handleClick}
      sx={{
        minHeight: {
          xs: 68,
          sm: 48,
        },
        p: 0,
        "&.Mui-selected": {
          bgcolor: "transparent",
          borderBottom: {
            xs: "2px solid",
            sm: "none",
          },
          borderBottomColor: {
            xs: "primary.main",
            sm: "transparent",
          },
        },
      }}
      divider={smDown ? true : false}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: 1,
          justifyContent: "center",
        }}
      >
        {icon}
      </ListItemIcon>

      <ListItemText
        primary={label}
        primaryTypographyProps={{
          sx: {
            lineHeight: 1.5,
            letterSpacing: "-0.3px",
            ...(active && {
              fontWeight: 600,
            }),
          },
        }}
        sx={{
          flexGrow: {
            xs: 1,
            sm: 0,
          },

          ...(active && {
            borderBottom: {
              xs: "none",
              sm: "2px solid",
            },
            borderBottomColor: {
              xs: "transparent",
              sm: "primary.main",
            },
          }),
        }}
      />
    </ListItemButton>
  )
}
